import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useListCarrierAddOnServices,
  useUpdateCarrierAddOnService,
  type UpdateCarrierAddOnServiceInput,
  type PackagingServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers } from "@/api/carriers";
import type { CarrierAddOnService } from "@/api.types.v2";
import { ServiceEnablementPanel } from "./service-enablement-panel";

/**
 * Tabbed detail view for a single PACKAGING add-on service. Three tabs:
 * Overview (code/name/active), Pricing (flat fee OR dimensional pricing —
 * perCubicCm + minimum), Enablement (reuses ServiceEnablementPanel with
 * type=PACKAGING). Mirrors InsuranceServiceDetailScreen's shape.
 */
export function PackagingServiceDetailScreen() {
  const { id } = useParams<{ id: string }>();

  const { data: services, isLoading: servicesLoading } =
    useListCarrierAddOnServices({ type: "PACKAGING" });
  const { data: carriersResp } = useGetCarriers();

  const service = id ? services?.find((s) => s.id === id) : undefined;
  const providerName =
    carriersResp?.data.find((c) => c.id === service?.providerCarrierId)?.name ??
    "—";

  if (servicesLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center p-8 text-muted-foreground">
          <p>Packaging service not found.</p>
          <Button asChild variant="link" size="sm" className="mt-2">
            <Link to="/add-on-services/packaging">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to Packaging Services
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <DetailHeader service={service} providerName={providerName} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="enablement">Enablement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab service={service} />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingTab service={service} />
        </TabsContent>
        <TabsContent value="enablement">
          <ServiceEnablementPanel serviceId={service.id} type="PACKAGING" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailHeader({
  service,
  providerName,
}: {
  service: CarrierAddOnService;
  providerName: string;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm">
        <Link
          to="/add-on-services/packaging"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Packaging Services
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold">{service.name}</h1>
          <code className="font-mono text-sm text-muted-foreground">
            {service.code}
          </code>
          {service.active ? (
            <Badge
              variant="secondary"
              className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
            >
              Inactive
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Provider: <span className="text-foreground">{providerName}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — dollars↔cents conversion + dirty-check parsing. Mirror the
// approach in the insurance detail screen: parse on Save, surface validation
// toasts, leave Save enabled for dirty-but-invalid input.
// ---------------------------------------------------------------------------

// Parse a dollars input field. Returns:
//   - null  when the field is blank (means "clear the cents value")
//   - undefined on invalid input (negative or non-numeric)
//   - cents integer otherwise (rounded)
function parseDollarsToCents(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

// Same for a raw non-negative number (e.g. perCubicCm rate). Returns null on
// blank, undefined on invalid, number otherwise.
function parseNonNegativeNumber(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

// cents → dollar string for display in the input.
function centsToDollarString(cents: number | null | undefined): string {
  if (cents == null) return "";
  return String(cents / 100);
}

// ---------------------------------------------------------------------------
// Overview tab — code (read-only), name, active. No extra packaging fields here.
// ---------------------------------------------------------------------------
function OverviewTab({ service }: { service: CarrierAddOnService }) {
  const [name, setName] = useState(service.name);
  const [active, setActive] = useState(service.active);

  // Re-seed only on :id change — same pattern as insurance Overview tab.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const s = serviceRef.current;
    setName(s.name);
    setActive(s.active);
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  const buildPatch = (): UpdateCarrierAddOnServiceInput | null => {
    const patch: UpdateCarrierAddOnServiceInput = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== service.name) patch.name = trimmedName;
    if (active !== service.active) patch.active = active;
    return Object.keys(patch).length === 0 ? null : patch;
  };

  const hasChanges = (() => {
    if (name.trim() && name.trim() !== service.name) return true;
    if (active !== service.active) return true;
    return false;
  })();

  const handleSave = () => {
    const patch = buildPatch();
    if (patch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: patch },
      {
        onSuccess: (updated) => {
          toast.success("Packaging service updated.");
          setName(updated.name);
          setActive(updated.active);
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update service.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Code <code className="font-mono">{service.code}</code> and provider
          carrier can&apos;t be changed. To retire, toggle off the Active
          switch.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="pk-detail-code">Code</Label>
          <Input
            id="pk-detail-code"
            value={service.code}
            readOnly
            disabled
            className="font-mono"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pk-detail-name">Name</Label>
          <Input
            id="pk-detail-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground">
              Inactive services are hidden from booking flows but preserved for
              existing references.
            </p>
          </div>
          <Button
            variant={active ? "outline" : "default"}
            size="sm"
            onClick={() => setActive(!active)}
          >
            {active ? (
              <>
                <X className="h-3 w-3 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reactivate
              </>
            )}
          </Button>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pricing tab — flat fee OR dimensional pricing (perCubicCm + minimum). Server
// upserts the details row; we always send a `packaging:` block on save. If
// neither input set is filled, that's `packaging: {}` (clears both).
// ---------------------------------------------------------------------------
function PricingTab({ service }: { service: CarrierAddOnService }) {
  const initialFlat = service.packagingDetails?.flatFeeCents ?? null;
  const initialDim = service.packagingDetails?.dimensionalPricing ?? null;
  const initialPerCubicCm = initialDim?.perCubicCm ?? null;
  const initialMinCents = initialDim?.minimumCents ?? null;

  const [flatFeeDollars, setFlatFeeDollars] = useState(
    centsToDollarString(initialFlat)
  );
  const [perCubicCm, setPerCubicCm] = useState(
    initialPerCubicCm != null ? String(initialPerCubicCm) : ""
  );
  const [minimumDollars, setMinimumDollars] = useState(
    centsToDollarString(initialMinCents)
  );

  // Re-seed on :id change.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const d = serviceRef.current.packagingDetails;
    setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
    const dim = d?.dimensionalPricing ?? null;
    setPerCubicCm(dim?.perCubicCm != null ? String(dim.perCubicCm) : "");
    setMinimumDollars(centsToDollarString(dim?.minimumCents ?? null));
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  // Build the `packaging:` block. Returns:
  //   undefined → invalid input (toast already fired)
  //   null      → nothing to send
  //   object    → packaging block to send (may be empty {} which clears both)
  const buildPackaging = (): PackagingServiceInput | null | undefined => {
    const packaging: PackagingServiceInput = {};

    // Flat fee — straight dollars→cents parse.
    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) {
      toast.error("Flat fee must be a non-negative number or blank.");
      return undefined;
    }

    // Dimensional pair — both fields required together if either set.
    const perCm = parseNonNegativeNumber(perCubicCm);
    if (perCm === undefined) {
      toast.error(
        "Dimensional rate (per cubic cm) must be a non-negative number or blank."
      );
      return undefined;
    }
    const minCents = parseDollarsToCents(minimumDollars);
    if (minCents === undefined) {
      toast.error(
        "Dimensional minimum must be a non-negative number or blank."
      );
      return undefined;
    }

    // Dimensional present means both are non-null in the resulting payload.
    // If only one of the pair is set, refuse the save — both required together.
    const dimensionalSet = perCm != null || minCents != null;
    if (dimensionalSet && (perCm == null || minCents == null)) {
      toast.error(
        "Dimensional pricing needs both per-cubic-cm and minimum — set both or clear both."
      );
      return undefined;
    }

    let flatDirty = false;
    let dimDirty = false;

    if (flat !== initialFlat) {
      packaging.flatFeeCents = flat;
      flatDirty = true;
    }

    const dimChanged =
      perCm !== initialPerCubicCm || minCents !== initialMinCents;
    if (dimChanged) {
      packaging.dimensionalPricing =
        dimensionalSet && perCm != null && minCents != null
          ? { perCubicCm: perCm, minimumCents: minCents }
          : null;
      dimDirty = true;
    }

    if (!flatDirty && !dimDirty) return null;
    return packaging;
  };

  const hasChanges = (() => {
    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) return true;
    if (flat !== initialFlat) return true;
    const perCm = parseNonNegativeNumber(perCubicCm);
    if (perCm === undefined) return true;
    if (perCm !== initialPerCubicCm) return true;
    const minCents = parseDollarsToCents(minimumDollars);
    if (minCents === undefined) return true;
    if (minCents !== initialMinCents) return true;
    return false;
  })();

  const handleSave = () => {
    const packaging = buildPackaging();
    if (packaging === undefined) return;
    if (packaging === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: { packaging } },
      {
        onSuccess: (updated) => {
          toast.success("Pricing updated.");
          const d = updated.packagingDetails;
          setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
          const dim = d?.dimensionalPricing ?? null;
          setPerCubicCm(dim?.perCubicCm != null ? String(dim.perCubicCm) : "");
          setMinimumDollars(centsToDollarString(dim?.minimumCents ?? null));
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update pricing.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing</CardTitle>
        <p className="text-sm text-muted-foreground">
          Flat fee OR dimensional pricing (per cubic cm + minimum floor). Mutual
          exclusion is not enforced — admin-discretion which pattern to use.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="pkp-flat">Flat fee (CAD)</Label>
          <Input
            id="pkp-flat"
            type="number"
            step="any"
            value={flatFeeDollars}
            onChange={(e) => setFlatFeeDollars(e.target.value)}
            placeholder="—"
          />
          <p className="text-xs text-muted-foreground">
            A fixed fee per package. Leave blank to clear.
          </p>
        </div>

        <div className="relative flex items-center">
          <div className="grow border-t" />
          <span className="mx-3 text-xs uppercase tracking-wide text-muted-foreground">
            OR
          </span>
          <div className="grow border-t" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="pkp-per-cm">Per cubic cm (¢)</Label>
            <Input
              id="pkp-per-cm"
              type="number"
              step="any"
              value={perCubicCm}
              onChange={(e) => setPerCubicCm(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              Cents per cm³ of package volume.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pkp-min">Minimum (CAD)</Label>
            <Input
              id="pkp-min"
              type="number"
              step="any"
              value={minimumDollars}
              onChange={(e) => setMinimumDollars(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              Floor on the dimensional charge.
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
